/**
 * Mapas de estado → etiqueta y color, para conversaciones y documentos.
 *
 * Fuente única: antes cada página tenía su propio mapa y el mismo
 * `HUMAN_REQUIRED` salía ámbar en una pantalla y rojo en otra.
 *
 * Sin JSX, para que `StatusPill.tsx` exporte solo su componente — lo pide
 * `react-refresh/only-export-components`.
 */
export type Tone = 'green' | 'amber' | 'red' | 'blue' | 'purple' | 'gray'

export const TONE_CLASS: Record<Tone, string> = {
  green:  'bg-brand-light text-brand-hover',
  amber:  'bg-warn-light text-warn border border-warn-border',
  red:    'bg-danger-light text-danger border border-danger-border',
  blue:   'bg-info-light text-info border border-info-border',
  purple: 'bg-purple-light text-purple border border-purple-border',
  gray:   'bg-surface-subtle text-ink-muted border border-surface-border',
}

export const CONVERSACION: Record<string, { label: string; tone: Tone }> = {
  NEW:              { label: 'Nueva',            tone: 'blue' },
  ACTIVE:           { label: 'Activa',           tone: 'green' },
  OPEN:            { label: 'Abierta',          tone: 'green' },
  WAITING_DOCUMENT: { label: 'Espera documento', tone: 'amber' },
  IN_REVIEW:        { label: 'En revisión',      tone: 'purple' },
  HUMAN_REQUIRED:   { label: 'Requiere asesor',  tone: 'red' },
  OPTED_OUT:        { label: 'Dio de baja',      tone: 'gray' },
  CLOSED:           { label: 'Cerrada',          tone: 'gray' },
}

export const DOCUMENTO: Record<string, { label: string; tone: Tone }> = {
  PENDING:            { label: 'Pendiente',   tone: 'gray' },
  UPLOADED:           { label: 'Subido',      tone: 'gray' },
  PROCESSING:         { label: 'Procesando',  tone: 'blue' },
  PENDING_DRIVE_SYNC: { label: 'Sincronizando', tone: 'blue' },
  // Lo escribe document_processor cuando el clasificador no pudo determinar el
  // tipo: el archivo va a _SIN_CLASIFICAR y necesita ojo humano.
  NEEDS_REVIEW:       { label: 'Por revisar', tone: 'amber' },
  AWAITING_REVIEW:    { label: 'Por revisar', tone: 'amber' },
  EVALUATED:          { label: 'Evaluado',    tone: 'blue' },
  // Gris: dice DÓNDE quedó guardado, no si sirve. En verde se leía como
  // validado junto al DNI que el agente había rechazado (TEST, 19-oct-2026);
  // el veredicto lo dice su propio chip (`veredicto.ts`).
  UPLOADED_TO_DRIVE:  { label: 'En Drive',    tone: 'gray' },
  READY:              { label: 'Listo',       tone: 'amber' },
  PUBLISHED:          { label: 'Aprobado',    tone: 'green' },
  // Lo escribe `document_reviewer` cuando una persona aprueba el documento
  // desde el portal. Faltaba y el portal lo mostraba crudo, «APPROVED»
  // (DEF-045, QA de TEST del 05-oct-2026). Su par es REJECTED.
  APPROVED:           { label: 'Aprobado',    tone: 'green' },
  REJECTED:           { label: 'Rechazado',   tone: 'red' },
  // DEF-025: el Step Function no pudo procesarlo; la conversación ya derivó
  // por ERROR_TECNICO y el COE tiene la alerta.
  PROCESSING_FAILED:  { label: 'Falló el proceso', tone: 'red' },
  // DEF-026: el PDF del cliente traía varios documentos y se separó; cada
  // parte es su propio documento. El original queda como referencia.
  SEPARADO:           { label: 'Separado en partes', tone: 'gray' },
  RETIRED:            { label: 'Retirado',    tone: 'gray' },
}

export function statusTone(status: string, kind: 'conversation' | 'document'): Tone {
  const mapa = kind === 'document' ? DOCUMENTO : CONVERSACION
  return mapa[status]?.tone ?? 'gray'
}
